/**
 * Keeps track of WHICH reader the operator is working on.
 *
 * A SmartBox can carry two or three readers, and both reader surfaces (the
 * box-detail card and the ticket panel) need the same selection behaviour, so
 * it lives here once rather than being written twice and drifting.
 *
 * The default is the lowest-numbered ACTIVE reader — the same reader the
 * server picks when a request omits `reader_id`, so the UI and the API never
 * disagree about what "no choice made" means. A box whose readers are all
 * switched off still selects one, because an operator needs to be able to talk
 * to a disabled reader in order to diagnose it.
 *
 * A selection survives a status refresh, which matters because every hardware
 * write triggers one: being bounced back to reader 1 after setting power on
 * reader 3 would be a good way to change the wrong reader next.
 */

import { useCallback, useEffect, useState } from "react";

import { type ReaderInfo } from "../services/readerApi";

interface UseSelectedReader {
  selectedId: number | null;
  selected: ReaderInfo | null;
  select: (readerId: number) => void;
}

export function useSelectedReader(readers: ReaderInfo[]): UseSelectedReader {
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const ids = readers.map((r) => r.reader_id).join(",");

  useEffect(() => {
    if (!readers.length) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    // Hold the current choice across refreshes, but drop it if that reader has
    // gone away (a reader row removed on the box, or a different box loaded
    // into the same component).
    if (selectedId != null && readers.some((r) => r.reader_id === selectedId)) return;
    const preferred = readers.find((r) => r.is_active) ?? readers[0];
    setSelectedId(preferred.reader_id);
  }, [ids, selectedId, readers]);

  const select = useCallback((readerId: number) => setSelectedId(readerId), []);

  const selected =
    readers.find((r) => r.reader_id === selectedId) ?? null;

  return { selectedId, selected, select };
}

export default useSelectedReader;
